import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { fetchQboRef } from "./api.js";

// Reusable searchable picker over a realm's cached QuickBooks reference list (Customer / Class / Account /
// Item / Vendor / Term). Stores the QBO id, shows the name. Falls back to a plain text input when there's no
// realm context (or nothing cached yet), so it degrades gracefully before the first sync.
//
// The open list is rendered in a portal with fixed positioning so it is never clipped by a scrolling
// drawer/modal or an overflow-hidden table wrapper (e.g. the template line-items / loan-splits tables).
//
//   <QboRefPicker realmId={realm} type="Class" value={loan.classId} name={loan.className}
//                 onChange={({ qboId, name }) => set({ classId: qboId, className: name })} />
export function QboRefPicker({ realmId, type, value, name, onChange, placeholder, subTypes }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);   // null = not loaded yet
  const [rect, setRect] = useState(null);      // field viewport rect — anchors the fixed-position portal
  const ref = useRef();       // the field wrapper
  const dropRef = useRef();   // the portaled dropdown

  // Close on an outside click — but the portaled dropdown lives outside `ref`, so exempt it too.
  useEffect(() => {
    const h = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (dropRef.current && dropRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // While open, keep the fixed dropdown anchored under the field as the drawer/page scrolls or resizes.
  useEffect(() => {
    if (!open) return;
    const place = () => { if (ref.current) setRect(ref.current.getBoundingClientRect()); };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [open]);

  useEffect(() => {
    if (!open || items !== null || !realmId) return;
    fetchQboRef(realmId, type).then((rows) => setItems(rows || [])).catch(() => setItems([]));
  }, [open, items, realmId, type]);

  const filtered = useMemo(() => {
    let list = (items || []).slice().sort((a, b) => a.name.localeCompare(b.name));   // FQN sort => parents sit right above their children
    if (subTypes && subTypes.length) list = list.filter((i) => subTypes.includes(i.subType));   // restrict to given QBO classifications (e.g. Expense)
    const s = q.trim().toLowerCase();
    list = s ? list.filter((i) => i.name.toLowerCase().includes(s)) : list;
    // Unlinked field that still carries a legacy name: float its exact match to the top (Enter-away linking).
    const target = !value && name ? name.trim().toLowerCase() : null;
    if (target) {
      const hit = list.filter((i) => i.name.trim().toLowerCase() === target);
      if (hit.length) list = [...hit, ...list.filter((i) => !hit.includes(i))];
    }
    return list.slice(0, 80);
  }, [items, q, subTypes, value, name]);
  const isExact = (i) => !value && name && i.name.trim().toLowerCase() === name.trim().toLowerCase();

  // No realm context -> plain text (still lets the user type a name for legacy/manual mapping).
  if (!realmId) {
    return <input className="input" value={name || value || ""} placeholder={placeholder}
                  onChange={(e) => onChange({ qboId: null, name: e.target.value })} />;
  }

  const label = name || (value ? `#${value}` : "");
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" className="input" style={{ textAlign: "left", cursor: "pointer", width: "100%" }}
              onClick={() => { if (!open && ref.current) setRect(ref.current.getBoundingClientRect()); setOpen((o) => !o); }}>
        {label || <span className="muted">{placeholder || "Select from QuickBooks…"}</span>}
      </button>
      {open && rect && createPortal(
        <div ref={dropRef} style={{ position: "fixed", zIndex: 9999, top: rect.bottom + 4, left: rect.left, width: rect.width,
                                    background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: 8,
                                    boxShadow: "0 8px 24px rgba(0,0,0,.18)", maxHeight: 280, overflowY: "auto" }}>
          <input autoFocus className="input" style={{ margin: 8, width: "calc(100% - 16px)" }} placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
          {items === null ? (
            <div className="muted" style={{ padding: "8px 12px", fontSize: 13 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="muted" style={{ padding: "8px 12px", fontSize: 13 }}>No matches — Refresh lists on the QuickBooks screen.</div>
          ) : (
            filtered.map((i) => {
              const parts = String(i.name).split(":");
              const depth = parts.length - 1;
              const leaf = parts[parts.length - 1];
              const searching = q.trim().length > 0;
              return (
                <div key={i.qboId} style={{ padding: "7px 12px", paddingLeft: searching || isExact(i) ? 12 : 12 + depth * 16, cursor: "pointer", fontSize: 13.5,
                                            background: isExact(i) ? "var(--accent-soft)" : "transparent" }}
                     onClick={() => { onChange({ qboId: i.qboId, name: i.name }); setOpen(false); setQ(""); }}>
                  {searching || isExact(i)
                    ? <span>{leaf}{depth > 0 && <span className="muted" style={{ fontSize: 11.5 }}> · {parts.slice(0, -1).join(" › ")}</span>}</span>
                    : <span style={{ color: depth === 0 ? "var(--color-text)" : "var(--color-text-secondary)", fontWeight: depth === 0 ? 500 : 400 }}>{leaf}</span>}
                  {isExact(i) && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: "var(--accent-ink)" }}>EXACT MATCH</span>}
                </div>
              );
            })
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
